import type { ReactNode, Ref } from 'react';
import { forwardRef } from 'react';

import * as RadioGroup from '@radix-ui/react-radio-group';

import { cn } from '../../../utils';
import type { FormControlProps } from '../FormControl/FormControl';
import { FormControl } from '../FormControl/FormControl';

export interface FormToggleButtonRadioGroupOptionData {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export type FormToggleButtonRadioGroupProps = RadioGroup.RadioGroupProps &
  FormControlProps & {
    inputRef?: Ref<HTMLInputElement>;
    options: FormToggleButtonRadioGroupOptionData[];
    wrapperClassName?: string;
    itemProps?: Omit<RadioGroup.RadioGroupItemProps, 'value'> & {
      className?: string;
      fullWidth?: boolean;
    };
  };

const FormToggleButtonRadioGroup = forwardRef(
  (
    {
      label,
      isError,
      tooltip,
      inputRef,
      hint,
      className = '',
      wrapperClassName = '',
      required,
      visuallyRequired,
      options,
      itemProps: { className: itemClassName, ...itemProps } = {},
      ...props
    }: FormToggleButtonRadioGroupProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    return (
      <FormControl
        hint={hint}
        label={label}
        // TODO: add id based that set on active option
        // htmlFor={id}
        isError={isError}
        visuallyRequired={visuallyRequired || required}
        tooltip={tooltip}
        ref={ref}
        className={wrapperClassName}
      >
        <RadioGroup.Root className={cn('msq-toggle-button-group', className)} {...props}>
          {options.map((option) => {
            return (
              <RadioGroup.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'w-full rounded-lg msq-toggle-button-group msq-toggle-button-item data-[state=checked]:msq-toggle-button-item-active whitespace-nowrap',
                  itemClassName,
                )}
                data-qa={'radiogroup-' + option.value}
                {...itemProps}
              >
                {option.label}
              </RadioGroup.Item>
            );
          })}
        </RadioGroup.Root>
      </FormControl>
    );
  },
);

FormToggleButtonRadioGroup.displayName = 'FormToggleButtonRadioGroup';

export { FormToggleButtonRadioGroup };
